import type {
  EventColor,
  EventCommitment,
  EventCondition,
  EventContact,
  EventFlowStep,
  EventInvoiceSource,
  EventRequirement,
  EventStatus,
  KeyTime,
  OperationEvent,
} from "../domain/operations";
import { apiFetch } from "./http";

type ApiEventDetail = {
  event: {
    id: string;
    name: string;
    type: string;
    status: EventStatus | "in_progress";
    color: EventColor;
    client: string;
    dateLabel: string;
    dateRangeLabel?: string | null;
    location?: string | null;
    headcount?: number | null;
    selectedService?: string | null;
    leadStaffId?: string | null;
  };
  contacts: Array<{
    id: string;
    name: string;
    role: string;
    phone?: string | null;
    external: boolean;
  }>;
  keyTimes: Array<{ id: string; label: string; timeLabel: string }>;
  flow: Array<{ id: string; label: string; timeLabel: string; detail: string }>;
  commitments: Array<{ id: string; title: string; detail: string }>;
  requirements: Array<{ id: string; title: string; detail: string; critical: boolean; resolved: boolean }>;
  conditions: Array<{ id: string; title: string; detail: string; severity: "info" | "caution" | "blocking" }>;
  menuItems: Array<{ id: string; displayName: string; rawLine: string; quantity?: string | null; unit?: string | null }>;
  sourceInvoice: {
    importId: string;
    sourceDocumentId?: string | null;
    sourceKind: "catalog" | "upload";
    filename: string;
    originalFilename?: string | null;
    invoiceNumber?: string | null;
    invoiceIssueDate?: string | null;
    importedAt: string;
    sourceDocumentSha256?: string | null;
    reviewedDraft: Record<string, unknown>;
  } | null;
};

function mapStatus(status: ApiEventDetail["event"]["status"]): EventStatus {
  if (status === "in_progress") return "confirmed";
  return status;
}

export async function fetchEventById(eventId: string): Promise<OperationEvent> {
  const detail = await apiFetch<ApiEventDetail>(`/events/${eventId}`);
  const event = detail.event;

  const sourceInvoice: EventInvoiceSource | undefined = detail.sourceInvoice
    ? {
        importCandidateId: detail.sourceInvoice.importId,
        sourceKind: detail.sourceInvoice.sourceKind,
        filename: detail.sourceInvoice.filename,
        invoiceNumber: detail.sourceInvoice.invoiceNumber ?? undefined,
        invoiceIssueDate: detail.sourceInvoice.invoiceIssueDate ?? undefined,
        importedAt: detail.sourceInvoice.importedAt,
        reviewedDraft: detail.sourceInvoice.reviewedDraft,
      }
    : undefined;

  const contacts: EventContact[] = detail.contacts.map((contact) => ({
    id: contact.id,
    name: contact.name,
    role: contact.role,
    phone: contact.phone ?? undefined,
    external: contact.external,
  }));

  const keyTimes: KeyTime[] = detail.keyTimes.map((entry) => ({
    id: entry.id,
    label: entry.label,
    timeLabel: entry.timeLabel,
  }));

  const flow: EventFlowStep[] = detail.flow.map((entry) => ({
    id: entry.id,
    label: entry.label,
    timeLabel: entry.timeLabel,
    detail: entry.detail,
  }));

  const commitments: EventCommitment[] = detail.commitments.map((entry) => ({
    id: entry.id,
    title: entry.title,
    detail: entry.detail,
  }));

  const requirements: EventRequirement[] = detail.requirements.map((entry) => ({
    id: entry.id,
    title: entry.title,
    detail: entry.detail,
    critical: entry.critical,
    resolved: entry.resolved,
  }));

  const conditions: EventCondition[] = detail.conditions.map((entry) => ({
    id: entry.id,
    title: entry.title,
    detail: entry.detail,
    severity: entry.severity,
  }));

  return {
    id: event.id,
    name: event.name,
    type: event.type,
    status: mapStatus(event.status),
    color: event.color,
    client: event.client,
    dateLabel: event.dateLabel,
    dateRangeLabel: event.dateRangeLabel ?? undefined,
    location: event.location ?? undefined,
    headcount: event.headcount ?? undefined,
    selectedService: event.selectedService ?? undefined,
    leadStaffId: event.leadStaffId ?? undefined,
    contacts,
    keyTimes,
    flow,
    commitments,
    requirements,
    conditions,
    sourceInvoice,
  };
}
